// The kiosk's own idle timer (UC-PATIENTS-022 A2; TEN-65; BR-PATIENTS-014). The shell's idle lock is not mounted in kiosk
// mode — it would lock a patient mid-form after the tablet idle time and could not clear the form — so the kiosk counts
// `handoverIdleMinutes` from the last touch, key or pointer move itself and, when they pass, discards what was typed, ends the
// handover on the device and locks the screen. Like session/idle-lock.ts the note of activity is throttled and the deadline is
// re-checked when the tab returns to the foreground, because a hidden tab's timers are throttled.
import { useEffect, useRef } from "react";

const ACTIVITY_EVENTS = ["pointerdown", "pointermove", "keydown", "touchstart", "wheel"] as const;
const ACTIVITY_THROTTLE_MS = 1_000;
const MAX_TIMEOUT_MS = 2_147_000_000;

export function useKioskIdle(minutes: number, onIdle: () => void, enabled = true): void {
  const idleRef = useRef(onIdle);
  idleRef.current = onIdle;
  useEffect(() => {
    if (!enabled) return;
    const idleMs = Math.max(1, minutes) * 60_000;
    let lastActivityAt = Date.now();
    let lastNoted = lastActivityAt;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let fired = false;
    const deadline = () => lastActivityAt + idleMs;
    const fire = () => {
      timer = undefined;
      if (fired) return;
      if (Date.now() >= deadline()) { fired = true; idleRef.current(); return; }
      arm();
    };
    const arm = () => {
      if (timer !== undefined) clearTimeout(timer);
      timer = setTimeout(fire, Math.min(Math.max(deadline() - Date.now(), 0), MAX_TIMEOUT_MS));
    };
    const onActivity = () => {
      const now = Date.now();
      if (now - lastNoted < ACTIVITY_THROTTLE_MS) return;
      lastNoted = now;
      lastActivityAt = now;
    };
    const onVisibility = () => { if (document.visibilityState === "visible" && Date.now() >= deadline()) fire(); };
    for (const ev of ACTIVITY_EVENTS) window.addEventListener(ev, onActivity, { passive: true, capture: true });
    document.addEventListener("visibilitychange", onVisibility);
    arm();
    return () => {
      if (timer !== undefined) clearTimeout(timer);
      for (const ev of ACTIVITY_EVENTS) window.removeEventListener(ev, onActivity, { capture: true });
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [minutes, enabled]);
}

/** How often, at most, the kiosk tells the server that the patient is still at the tablet (BR-PATIENTS-028). */
export const KIOSK_ACTIVITY_EVERY_MS = 60_000;

/**
 * While the screen is being touched, `onActive` runs at most once every `everyMs` (BR-PATIENTS-028). The server's handover expiry
 * rolls only with the kiosk's requests, and a step that makes none — the details, a long medical history, the photos — would
 * otherwise let it lapse under a patient who is still filling in the form. Untouched, nothing is sent and the idle timer above
 * runs out as before; the first signal goes a full interval after the hook starts, the handover having just been entered.
 */
export function useKioskKeepAlive(onActive: () => void, enabled = true, everyMs = KIOSK_ACTIVITY_EVERY_MS): void {
  const activeRef = useRef(onActive);
  activeRef.current = onActive;
  // Kept across a pause (offline): the first touch after a long one signals at once, when the handover needs it most.
  const lastSent = useRef<number | null>(null);
  if (lastSent.current === null) lastSent.current = Date.now();
  useEffect(() => {
    if (!enabled) return;
    const onActivity = () => {
      const now = Date.now();
      if (now - lastSent.current! < everyMs) return;
      lastSent.current = now;
      activeRef.current();
    };
    for (const ev of ACTIVITY_EVENTS) window.addEventListener(ev, onActivity, { passive: true, capture: true });
    return () => { for (const ev of ACTIVITY_EVENTS) window.removeEventListener(ev, onActivity, { capture: true }); };
  }, [enabled, everyMs]);
}
